import type { DocPageContent } from '../docs-content'

const page = {
  order: 22,
  slug: 'troubleshooting',
  title: '故障排除',
  description: '针对浏览器、工作区、媒体、WebGPU、本地 AI 与导出问题的修复方法。',
  category: '参考',
  related: ['workspaces', 'export'],
  sections: [
    {
      title: '浏览器与工作区',
      blocks: [
        {
          kind: 'table',
          headers: ['症状', '修复方法'],
          rows: [
            [
              '不支持的浏览器，或没有工作区选择器',
              '使用 Chrome 或 Edge 113+，它们支持精剪所需的 API。',
            ],
            [
              'Brave 无法选择文件夹',
              '启用 `brave://flags/#file-system-access-api` 并重新启动 Brave。',
            ],
            [
              '权限被拒绝',
              '选择一个你可以编辑的普通文件夹，然后允许读写访问。',
            ],
            [
              '重新打开时被要求重新连接',
              '为同一文件夹选择**重新连接**；如果它已被移动，则选择另一个文件夹。',
            ],
          ],
        },
      ],
    },
    {
      title: '媒体与导入',
      blocks: [
        {
          kind: 'table',
          headers: ['症状', '修复方法'],
          rows: [
            [
              '缺失媒体',
              '使用**授予访问权限**、**定位**、**定位文件夹**或**浏览其他文件夹**重新链接。',
            ],
            [
              '导入失败或不支持的编解码器',
              '尝试另一种浏览器支持的格式，或先对文件进行转码。',
            ],
            [
              '通过 URL 导入失败',
              '确保链接指向媒体文件本身，而不是嵌入该文件的页面。',
            ],
            [
              '预览或波形过期',
              '重新生成缩略图或代理文件，或在存储设置中清除项目缓存。',
            ],
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '仅在打算稍后重新链接文件时才使用**离线工作**——在此之前，片段会一直保持为损坏的引用。',
        },
      ],
    },
    {
      title: 'WebGPU 与本地 AI',
      blocks: [
        {
          kind: 'table',
          headers: ['症状', '修复方法'],
          rows: [
            [
              '特效、示波器或 AI 不可用',
              'WebGPU 可能已关闭——请使用较新的 Chrome 或 Edge，更新 GPU 驱动，并保持硬件加速开启。',
            ],
            [
              '模型下载缓慢或失败',
              '保持标签页打开，在稳定的连接下重试，并在下载损坏时清除**本地 AI 模型缓存**。',
            ],
            ['AI 工作时内存不足', '使用**卸载本地模型**释放运行时。'],
            ['LUT 导入被阻止', 'LUT（.cube）导入需要基于 Chromium 内核的浏览器。'],
          ],
        },
      ],
    },
    {
      title: '导出',
      blocks: [
        {
          kind: 'table',
          headers: ['症状', '修复方法'],
          rows: [
            [
              '起飞前检查提示编解码器不可用',
              '选择另一种编解码器或格式，或降低分辨率或质量。',
            ],
            [
              'Worker 导出不可用',
              '保持标签页聚焦并避免大量交互；用视频片段替换动画图像项可能会有帮助。',
            ],
            [
              '导出文件过大或缓慢',
              '先导出更短的入/出范围，降低分辨率或质量，并释放磁盘空间。',
            ],
            [
              '导出无法开始',
              '清除任何阻塞性的起飞前警告，然后尝试一小段范围以确认管道正常工作。',
            ],
          ],
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
